import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

const NotificationBell = ({ isAdmin = false }) => {
  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch notification count
  const fetchCount = async () => {
    try {
      const response = await api.get('/notifications/count');
      if (response.data.success) {
        setCount(response.data.count);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  // Fetch unread notifications when dropdown opens
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications/unread');
      if (response.data.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark single notification as read and navigate
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await api.put(`/notifications/${notification.id}/read`);

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setCount(prev => Math.max(0, prev - 1));

      // Close dropdown
      setShowDropdown(false);

      // Navigate to the item
      const path = getNavigationPath(notification);
      if (path) {
        navigate(path);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications([]);
      setCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Get navigation path based on notification type and user role
  const getNavigationPath = (notification) => {
    if (isAdmin) {
      switch (notification.type) {
        case 'task':
          return `/admin-dashboard/tasks`;
        case 'event':
          return `/admin-dashboard/events`;
        case 'document':
          return `/admin-dashboard/documents`;
        default:
          return null;
      }
    }
    switch (notification.type) {
      case 'task':
        return `/my-tasks`;
      case 'event':
        return `/calendar`;
      case 'document':
        return `/documents`;
      default:
        return null;
    }
  };

  // Load count on mount and when route changes
  useEffect(() => {
    fetchCount();
    // Poll for new notifications every 15 seconds
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Load notifications and calculate position when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      fetchNotifications();
      // Calculate dropdown position based on button position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = 320;
        let leftPos = rect.right - dropdownWidth;
        if (leftPos < 8) leftPos = 8;
        setDropdownPosition({
          top: rect.bottom + 8,
          left: leftPos
        });
      }
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format time ago
  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Get icon for notification type
  const getIcon = (type) => {
    switch (type) {
      case 'event':
        return 'E';
      case 'task':
        return 'T';
      case 'document':
        return 'D';
      default:
        return 'N';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
        title="Notifications"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Badge */}
        {count > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="fixed w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-[9999]"
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No new notifications
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        <span className="text-xl">{getIcon(notification.type)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-xs text-gray-500 truncate mt-1">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {timeAgo(notification.created_at)}
                          {notification.created_by_first_name && (
                            <span> • by {notification.created_by_first_name} {notification.created_by_last_name}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <span className="w-2 h-2 bg-primary-500 rounded-full inline-block"></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-gray-200 text-center">
            <Link
              to={isAdmin ? '/admin-dashboard/notifications' : '/notifications'}
              onClick={() => setShowDropdown(false)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
